import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Zap, AlertTriangle, ShieldCheck, Clock, ChevronLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/SEOHead';
import { CornerAccents } from '@/components/CornerAccents';
import { Reveal } from '@/components/motion/Reveal';
import { SpotlightCard } from '@/components/motion/SpotlightCard';

const VW_URL = 'https://vowpace.com';
const VW_ICON = '/press-kit/vowpace-icon.png';
const HEADING_FONT = { fontFamily: "'Plus Jakarta Sans', 'Noto Sans TC', sans-serif" };
const BODY_FONT = { fontFamily: "'Manrope', 'Noto Sans TC', sans-serif" };

const content = {
  'zh-TW': {
    badge: '線上 SaaS 工具',
    name: 'Vowpace AI',
    tagline: '專為美國商業分包商而設的簽約前分包合約條款風險審查 — 簽署前，及早發現可能的合約風險。',
    hero_sub: '免密碼・簽約前分包合約條款風險審查',
    visit: '啟動應用',
    about_label: '關於 Vowpace AI',
    about: 'Vowpace AI 是專為美國商業分包商而設的簽約前分包合約條款風險審查工具。上傳任何分包合約，AI 會自動標記隱藏的違約賠償金、概括性彌償與有條件付款（pay-when-paid）條款 — 助分包商在簽署前審視合約風險。AI 輔助分析；不構成法律意見。',
    features_label: '核心功能',
    features: [
      { title: '免密碼零阻力掃描', desc: '上傳合約即可取得完整風險報告 — 無需設定密碼、無需填表，即傳即審即預覽。' },
      { title: '違約賠償金偵測', desc: '自動找出未設上限的違約賠償金（Liquidated Damages）與高風險罰則條款。' },
      { title: '彌償條款把關', desc: '在承擔責任之前，標記概括性彌償（Broad-form Indemnity）與一面倒的風險轉嫁條款。' },
      { title: '有條件付款條款', desc: '在簽署前標記 pay-if-paid 與 pay-when-paid 付款條件條款，認清收款風險。' },
    ],
    why_label: '為何需要 Vowpace',
    why: '一條被忽略的條款，足以蝕掉整個商業專案的利潤。Vowpace 以自動掃描審視合約，助你在簽署之前守住專案利潤。',
    cta_title: '簽署之前，先掃描一次',
    cta_desc: '及早發現合約中可能的風險。',
    ill: '示意範例',
    back: '產品展示',
  },
  'zh-CN': {
    badge: '线上 SaaS 工具',
    name: 'Vowpace AI',
    tagline: '专为美国商业分包商而设的签约前分包合同条款风险审查 — 签署前，及早发现可能的合同风险。',
    hero_sub: '免密码・签约前分包合同条款风险审查',
    visit: '启动应用',
    about_label: '关于 Vowpace AI',
    about: 'Vowpace AI 是专为美国商业分包商而设的签约前分包合同条款风险审查工具。上传任何分包合同，AI 会自动标记隐藏的违约赔偿金、概括性弥偿与有条件付款（pay-when-paid）条款 — 助分包商在签署前审视合同风险。AI 辅助分析；不构成法律意见。',
    features_label: '核心功能',
    features: [
      { title: '免密码零阻力扫描', desc: '上传合同即可取得完整风险报告 — 无需设置密码、无需填表，即传即审即预览。' },
      { title: '违约赔偿金检测', desc: '自动找出未设上限的违约赔偿金（Liquidated Damages）与高风险罚则条款。' },
      { title: '弥偿条款把关', desc: '在承担责任之前，标记概括性弥偿（Broad-form Indemnity）与一面倒的风险转嫁条款。' },
      { title: '有条件付款条款', desc: '在签署前标记 pay-if-paid 与 pay-when-paid 付款条件条款，认清收款风险。' },
    ],
    why_label: '为何需要 Vowpace',
    why: '一条被忽略的条款，足以蚀掉整个商业项目的利润。Vowpace 以自动扫描审视合同，助你在签署之前守住项目利润。',
    cta_title: '签署之前，先扫描一次',
    cta_desc: '及早发现合同中可能的风险。',
    ill: '示意示例',
    back: '产品展示',
  },
  en: {
    badge: 'Live SaaS Tool',
    name: 'Vowpace AI',
    tagline: 'Pre-signature subcontract clause-risk review for US commercial subcontractors — spot likely contract risks before you sign.',
    hero_sub: 'Passwordless · Pre-signature Subcontract Clause-Risk Review',
    visit: 'Launch App',
    about_label: 'About Vowpace AI',
    about: 'Vowpace AI is a pre-signature subcontract clause-risk review tool for US commercial subcontractors. Upload any subcontract and the AI automatically flags hidden liquidated damages, broad-form indemnities, and pay-when-paid clauses — helping subcontractors review contract risk before signing. AI-assisted; not legal advice.',
    features_label: 'Core Features',
    features: [
      { title: 'Passwordless & Frictionless Scan', desc: 'Upload a contract and receive a full risk report — zero password setups, zero forms, and instant preview.' },
      { title: 'Liquidated Damages Detection', desc: 'Surfaces un-capped liquidated damages and dangerous penalty clauses.' },
      { title: 'Indemnity Safeguards', desc: 'Flags broad-form indemnities and one-sided risk-transfer clauses before taking on liability.' },
      { title: 'Pay-if-paid / Pay-when-paid', desc: 'Flags pay-if-paid and pay-when-paid payment-condition clauses before you sign.' },
    ],
    why_label: 'Why Vowpace',
    why: 'A single overlooked clause can wipe out the profit of an entire commercial project. Vowpace runs an automated contract scan, helping you protect project margins before signature.',
    cta_title: 'Scan before you sign',
    cta_desc: 'Spot likely contract risks before you sign.',
    ill: 'Illustrative example',
    back: 'Products',
  },
};

const featureIcons = [Zap, AlertTriangle, ShieldCheck, Clock];

const TERMINAL_LINES = [
  { text: '$ vowpace scan subcontract_v3.pdf', color: '#94A3B8' },
  { text: '✓ clauses parsed', color: '#10B981' },
  { text: '⚠ LD clause §8.7 — uncapped exposure', color: '#F59E0B' },
  { text: '⚠ pay-if-paid — §9.3', color: '#F59E0B' },
  { text: '✓ risk report ready → risk_report.pdf', color: '#10B981' },
];

function VisitButton({ label, large = false }) {
  return (
    <a href={VW_URL} target="_blank" rel="noopener noreferrer">
      <Button
        data-testid={large ? 'vw-visit-btn-bottom' : 'vw-visit-btn'}
        className={`${large ? 'h-14 px-10 text-base' : 'h-12 px-8 text-sm'} rounded-full bg-[#6366F1] text-white font-bold hover:brightness-110 transition-all shadow-[0_8px_28px_rgba(99,102,241,0.35)]`}
      >
        {label}
        <ArrowUpRight className="ml-2 h-4 w-4" />
      </Button>
    </a>
  );
}

function HeroPreview({ subtitle }) {
  return (
    <SpotlightCard className="rounded-2xl">
      <div className="relative rounded-2xl overflow-hidden border border-slate-800/60 bg-[#0E131F] shadow-[0_24px_60px_rgba(0,0,0,0.5)]">
        <CornerAccents />
        <div className="h-8 flex items-center gap-1.5 px-4 bg-[#0E131F] border-b border-slate-800/60">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-[#64748B]">vowpace.com</span>
        </div>
        <div className="relative" data-testid="vw-preview-card">
          <div className="px-6 py-12 md:px-10 md:py-16 text-center bg-gradient-to-b from-[#0E131F] to-[#0B0F17]">
            <p className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#F8FAFC]" style={HEADING_FONT}>Vowpace</p>
            <p className="mt-3 text-sm md:text-base text-[#94A3B8] max-w-md mx-auto leading-relaxed">Pre-signature subcontract clause-risk review for US commercial subcontractors</p>
            <p className="mt-2 text-xs text-[#64748B]">AI-assisted analysis; not legal advice.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {['LD §8.7', 'PAY-IF-PAID §9.3', 'INDEMNITY'].map((tag) => (
                <span key={tag} className="px-2 py-1 rounded font-mono text-[9px] font-bold tracking-wider text-[#F59E0B] bg-[#080B10]/80 backdrop-blur border border-[#F59E0B]/40">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div data-testid="vw-hero-subtitle" className="flex items-center justify-center gap-2 px-4 py-2.5 border-t border-slate-800/60 bg-white/[0.02]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#6366F1]" style={{ animation: 'pulse-dot 2s ease-in-out infinite' }} />
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-[#818CF8]">{subtitle}</span>
        </div>
      </div>
    </SpotlightCard>
  );
}

function Terminal() {
  return (
    <SpotlightCard className="rounded-2xl max-w-3xl mx-auto">
      <div data-testid="vw-terminal" className="relative rounded-2xl bg-[#0E131F] border border-slate-800/60 overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.4)]">
        <CornerAccents />
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800/60 bg-white/[0.02]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          </div>
          <span className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-[#64748B]">VOWPACE / SCAN_ENGINE</span>
        </div>
        <div className="p-6 md:p-8 font-mono text-xs md:text-sm space-y-2.5">
          {TERMINAL_LINES.map((line, i) => (
            <motion.p
              key={line.text}
              initial={{ opacity: 0, x: 12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.35, duration: 0.4 }}
              style={{ color: line.color }}
            >
              {line.text}
            </motion.p>
          ))}
          <span className="inline-block w-2 h-4 bg-[#6366F1] align-middle" style={{ animation: 'blip 1s step-end infinite' }} />
        </div>
      </div>
    </SpotlightCard>
  );
}

export default function VowpacePage() {
  const { lang } = useLanguage();
  const c = content[lang] || content.en;

  return (
    <div data-testid="vowpace-page" className="bg-[#080B10] text-white min-h-screen">
      <SEOHead path="/products/vowpace" />

      <section className="relative min-h-[92vh] flex items-center overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          <img src="/img/hero-architecture.jpg" alt="" aria-hidden className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#080B10]/[0.9]" />
          <div
            className="absolute inset-0"
            style={{ background: 'radial-gradient(640px circle at 15% 25%, rgba(99,102,241,0.14), transparent 55%), radial-gradient(560px circle at 85% 75%, rgba(245,158,11,0.08), transparent 55%)' }}
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            className="lg:col-span-6"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="px-3 py-1.5 rounded-full bg-[#6366F1]/10 border border-[#6366F1]/30 text-xs font-bold text-[#818CF8] tracking-wide">{c.badge}</span>
            </div>
            <div className="flex items-center gap-5 mb-6">
              <img src={VW_ICON} alt="Vowpace AI" data-testid="vw-detail-icon" className="w-16 h-16 md:w-20 md:h-20 rounded-2xl shadow-[0_0_30px_rgba(99,102,241,0.45)]" />
              <div>
                <h1 data-testid="vw-detail-title" className="text-4xl md:text-5xl lg:text-6xl tracking-tighter font-bold text-[#F8FAFC]" style={HEADING_FONT}>
                  {c.name}
                </h1>
                <p className="text-[#64748B] font-mono text-xs mt-1 tracking-widest">vowpace.com</p>
              </div>
            </div>
            <p data-testid="vw-detail-tagline" className="text-base md:text-lg text-[#94A3B8] max-w-lg leading-relaxed mb-8" style={BODY_FONT}>
              {c.tagline}
            </p>
            <VisitButton label={c.visit} />
          </motion.div>
          <motion.div
            className="lg:col-span-6 hidden lg:block"
            initial={{ opacity: 0, y: 32, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.25, ease: [0.21, 0.47, 0.32, 0.98] }}
          >
            <HeroPreview subtitle={c.hero_sub} />
          </motion.div>
        </div>
      </section>

      <section data-testid="vw-about-section" className="py-24 md:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-4">{c.about_label}</p>
            <p data-testid="vw-about-text" className="text-xl md:text-2xl tracking-tight font-semibold text-[#F8FAFC]/85 max-w-3xl leading-relaxed" style={HEADING_FONT}>
              {c.about}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Terminal />
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{c.ill}</p>
          </Reveal>
        </div>
      </section>

      <section data-testid="vw-features-section" className="py-24 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-12">{c.features_label}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {c.features.map((feat, i) => {
              const Icon = featureIcons[i];
              return (
                <Reveal key={feat.title} delay={i * 0.08}>
                  <SpotlightCard className="rounded-2xl md:rounded-3xl h-full">
                    <div data-testid={`vw-feature-card-${i}`} className="h-full bg-[#0E131F] border border-slate-800/60 rounded-2xl md:rounded-3xl p-8">
                      <div className="w-12 h-12 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/25 flex items-center justify-center mb-5">
                        <Icon className="h-6 w-6 text-[#818CF8]" />
                      </div>
                      <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2" style={HEADING_FONT}>{feat.title}</h3>
                      <p className="text-sm text-[#94A3B8] leading-relaxed" style={BODY_FONT}>{feat.desc}</p>
                    </div>
                  </SpotlightCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="vw-why-section" className="py-24 md:py-28 border-t border-slate-800/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-[#818CF8] mb-6">{c.why_label}</p>
            <p data-testid="vw-why-text" className="text-xl md:text-2xl tracking-tight font-semibold text-[#F8FAFC]/80 max-w-3xl mx-auto leading-relaxed" style={HEADING_FONT}>
              {c.why}
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="vw-cta-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative rounded-3xl bg-[#0E131F] border border-slate-800/60 px-8 py-14 md:px-16 md:py-20 text-center overflow-hidden">
              <CornerAccents />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'radial-gradient(480px circle at 25% 30%, rgba(99,102,241,0.14), transparent 60%), radial-gradient(480px circle at 75% 70%, rgba(245,158,11,0.08), transparent 60%)' }}
              />
              <div className="relative z-10">
                <img src={VW_ICON} alt="Vowpace AI" className="w-14 h-14 rounded-2xl mx-auto mb-6 shadow-[0_0_24px_rgba(99,102,241,0.45)]" />
                <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#F8FAFC] mb-4" style={HEADING_FONT}>{c.cta_title}</h2>
                <p className="text-base md:text-lg text-[#94A3B8] mb-8" style={BODY_FONT}>{c.cta_desc}</p>
                <div className="flex justify-center"><VisitButton label={c.visit} large /></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <Link to="/products" data-testid="vw-back-to-products" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-white transition-colors">
          <ChevronLeft className="h-4 w-4" />
          {c.back}
        </Link>
      </div>
    </div>
  );
}
